import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import axios from 'axios'
import { AuthMissingError } from '@/utils/errors'
import { getPersistentAuth } from '@/utils/firebase-core'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const uid = ref<string | null>(null)
  const email = ref<string | null>(null)
  const typesenseKey = ref<string | null>(null)
  const ownedCollection = ref<string | null>(null)
  const isLogged = computed(() => refreshToken.value !== null || email.value !== null)

  function setAccessToken(token: string | null) { accessToken.value = token }
  function setRefreshToken(token: string | null) { refreshToken.value = token }
  function setUid(id: string | null) { uid.value = id }
  function setEmail(e: string | null) { email.value = e }
  function setTypesenseKey(key: string | null) { typesenseKey.value = key }
  function setOwnedCollection(collection: string | null) { ownedCollection.value = collection }

  function login(token: string) {
    try {
      const decoded = jwtDecode<{ user_id: string }>(token)
      uid.value = decoded.user_id
    } catch (error) {
      console.log(error)
      return false
    }
    refreshToken.value = token
  }

  let refreshPromise: Promise<void> | null = null

  async function refreshAccessToken() {
    if (refreshToken.value === null) {
      throw new AuthMissingError('No refresh token in store')
    }
    if (refreshPromise) {
      return refreshPromise
    }
    const tokenAtStart = refreshToken.value
    refreshPromise = (async () => {
      try {
        const response = await axios.post(
          '/users/refresh/',
          { refresh: tokenAtStart },
          {
            baseURL: `${import.meta.env.VITE_API_URL}/rest/v1`,
            // @ts-expect-error — consumed by axios-auth-refresh if this is ever
            // switched to axiosInstance; prevents interceptor re-entry on 401.
            skipAuthRefresh: true
          }
        )
        accessToken.value = response.data.access
      } catch (error: any) {
        const status = error?.response?.status
        const code = error?.response?.data?.code
        const terminal = status === 401 || status === 403
          || code === 'token_not_valid' || code === 'invalid_token'
        if (terminal) {
          accessToken.value = null
          refreshToken.value = null
        }
        throw new AuthMissingError('Failed to fetch token from backend')
      } finally {
        refreshPromise = null
      }
    })()
    return refreshPromise
  }

  async function fetchTypesenseKey() {
    try {
      // Import lazily to avoid circular dependency
      const { axiosBaseInstance } = await import('@/utils/axios-instance')
      const response = await axiosBaseInstance.get('/search/typesense-key/')
      const { useBrowsingStore } = await import('@/stores/useBrowsingStore')
      const browsingStore = useBrowsingStore()
      browsingStore.resetOwnedState()
      typesenseKey.value = response.data.key
      ownedCollection.value = response.data.owned_collection
    } catch (e) {
      console.error(e)
    }
  }

  async function logout() {
    const { useBrowsingStore } = await import('@/stores/useBrowsingStore')
    const browsingStore = useBrowsingStore()
    browsingStore.resetOwnedState()

    uid.value = null
    accessToken.value = null
    refreshToken.value = null
    email.value = null
    typesenseKey.value = null
    ownedCollection.value = null

    // Also clear root store user
    const { useRootStore } = await import('@/stores/useRootStore')
    const rootStore = useRootStore()
    rootStore.user = null

    const auth = await getPersistentAuth()
    await auth.signOut()
  }

  return {
    accessToken, refreshToken, uid, email, typesenseKey, ownedCollection,
    isLogged,
    setAccessToken, setRefreshToken, setUid, setEmail, setTypesenseKey, setOwnedCollection,
    login, refreshAccessToken, fetchTypesenseKey, logout
  }
}, {
  persist: {
    paths: ['accessToken', 'refreshToken', 'uid', 'email', 'typesenseKey', 'ownedCollection']
  }
})
